import { call, put, take, takeLatest } from "redux-saga/effects";
import actions from "../actionType";
import { notification } from "../../util/notification";
import formatMsg from "../../util/formatMsg";
import Helper from "../../util/helper";
import dbRef from "../../firebase-api/dbRef";
import fbRef from "../../libs/fbRef";
import { timeutil } from "../..";
import { callApi } from "../../util/superAgentUtil";
import { AttendanceEntityType } from "../../types/attendance";
import bugsnagClient from "@bugsnag/js";



// Attendance records come from the API; the channel is lastUpdate/attendance, which every
// attendance write bumps, so each emission is answered with a fresh fetch.
function* fetchAttendanceRecords(firebase, entityType: "PARTICIPANT" | "INSTRUCTOR", filter: string) {
    const endpoint = `bookingoApi/attendance/records?entityType=${entityType}&${filter}`;
    const response = yield call(callApi, firebase, "get", endpoint, {}, false, false, true);
    return response?.body?.data || [];
}

function* getParticipantsSlotAttendance(actionObj: Record<string, any>) {

    let participantsSlotAttendanceChannel: any;

    try {
        const { firebase, selectedSchedule } = actionObj.payload;
        const { slotRandomId, centerId, date } = selectedSchedule;

        const orderByKeyValue = `${timeutil.inFormat(date, "DD-MM-YY")}--${slotRandomId}--${centerId}`;

        participantsSlotAttendanceChannel = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateAttendance);

        while (true) {
            yield take(participantsSlotAttendanceChannel);
            const participantsAttendance = yield call(fetchAttendanceRecords, firebase, "PARTICIPANT", `dateSlotCenterKey=${encodeURIComponent(orderByKeyValue)}`);
            const participantsAttendanceMap = new Map();
            participantsAttendance.forEach((obj) => participantsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    isLoading: false,
                    participantsAttendanceMap,
                    participantsAttendanceChannel: participantsSlotAttendanceChannel
                }
            })
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch slot attendance of participants via channel", err);
        bugsnagClient.notify(err);
    }
    finally {
        if (participantsSlotAttendanceChannel) {
            participantsSlotAttendanceChannel.close();
        }
    }
}


function* getInstructorsSlotAttendance(actionObj: Record<string, any>) {

    let instructorsSlotAttendanceChannel: any;

    try {
        const { firebase, selectedSchedule } = actionObj.payload;
        const { slotRandomId, centerId, date } = selectedSchedule;

        const orderByKeyValue = `${timeutil.inFormat(date, "DD-MM-YY")}--${slotRandomId}--${centerId}`;

        instructorsSlotAttendanceChannel = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateAttendance);

        while (true) {
            yield take(instructorsSlotAttendanceChannel);
            const instructorsAttendance = yield call(fetchAttendanceRecords, firebase, "INSTRUCTOR", `dateSlotCenterKey=${encodeURIComponent(orderByKeyValue)}`);
            const instructorsAttendanceMap = new Map();
            instructorsAttendance.forEach((obj) => instructorsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    isLoading: false,
                    instructorsAttendanceMap,
                    instructorsAttendanceChannel: instructorsSlotAttendanceChannel
                }
            })
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch slot attendance of instructors via channel", err);
        bugsnagClient.notify(err);
    }
    finally {
        if (instructorsSlotAttendanceChannel) {
            instructorsSlotAttendanceChannel.close();
        }
    }
}


function buildSlotAttendancePayloadArr(entityType: AttendanceEntityType, tempAttendanceMap: Map<string, any>) {

    switch (entityType) {
        case 'INSTRUCTOR':
            const instructorAttendance: any[] = [];
            tempAttendanceMap.forEach((val, key) => instructorAttendance.push({ ...val }));
            return instructorAttendance;

        case 'PARTICIPANT':
            const participantAttendance: any[] = [];
            tempAttendanceMap.forEach((val, key) => participantAttendance.push({ ...val }));
            return participantAttendance;
    }
}


function* markSlotBulkAttendance(actionObj: Record<string, any>) {

    try {
        const { firebase, payload } = actionObj.payload;

        const {
            selectedSchedule,
            tempInstructorsAttendanceMap,
            tempParticipantsAttendanceMap
        } = payload;

        const finalPayload = {
            bulkCommonFields: {
                date: selectedSchedule.date,
                batchId: selectedSchedule.batchId,
                packageId: selectedSchedule.packageId,
                slotRandomId: selectedSchedule.slotRandomId,
                centerId: selectedSchedule.centerId
            },
            instructorAttendance: buildSlotAttendancePayloadArr('INSTRUCTOR', tempInstructorsAttendanceMap),
            participantAttendance: buildSlotAttendancePayloadArr('PARTICIPANT', tempParticipantsAttendanceMap),
            createdBy: firebase.teacher.id,
            createdOn: timeutil.getEpoch()
        }

        let endpoint = 'bookingoApi/attendance/markBulkAttendance';
        let response = yield call(callApi, firebase, "post", endpoint, finalPayload, false);

        if (response.status == 200 && response.body.data.status === "MARKED") {
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { isLoading: false, operationType: "BULK_ATTENDANCE_MARKED_SUCCESSFULLY" }
            });
            notification('success', formatMsg('attendanceMarkedSuccessfully'));
        }
        else {
            yield put({
                type: actions.ATTENDANCE_SAGA_FAILED,
                payload: { operationType: "BULK_ATTENDANCE_MARKING_FAILED" }
            });
            notification('error', formatMsg('failedToMarkAttedance'));
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to mark bulk attendance", err);
        bugsnagClient.notify(err);
    }
}


function* getParticipantsDateRangeAttendance(actionObj: Record<string, any>) {

    let participantsDateRangeAttendanceChannel: any;

    try {
        const { firebase, startDate, endDate } = actionObj.payload;

        participantsDateRangeAttendanceChannel = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateAttendance);

        while (true) {
            yield take(participantsDateRangeAttendanceChannel);
            const participantsAttendance = yield call(fetchAttendanceRecords, firebase, "PARTICIPANT", `startDate=${startDate}&endDate=${endDate}`);
            const participantsAttendanceMap = new Map();
            participantsAttendance.forEach((obj) => participantsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    attendanceLoading: false,
                    participantsAttendanceMap,
                    participantsAttendanceChannel: participantsDateRangeAttendanceChannel
                }
            })
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch date range attendance of participants via channel", err);
        bugsnagClient.notify(err);
    }
    finally {
        if (participantsDateRangeAttendanceChannel) {
            participantsDateRangeAttendanceChannel.close();
        }
    }
}


function* getInstructorsDateRangeAttendance(actionObj: Record<string, any>) {

    let instructorsDateRangeAttendanceChannel: any;

    try {
        const { firebase, startDate, endDate } = actionObj.payload;

        instructorsDateRangeAttendanceChannel = yield call(dbRef.getLastUpdateChannel, firebase, fbRef.lastUpdateAttendance);

        while (true) {
            yield take(instructorsDateRangeAttendanceChannel);
            const instructorsAttendance = yield call(fetchAttendanceRecords, firebase, "INSTRUCTOR", `startDate=${startDate}&endDate=${endDate}`);
            const instructorsAttendanceMap = new Map();
            instructorsAttendance.forEach((obj) => instructorsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    attendanceLoading: false,
                    instructorsAttendanceMap,
                    instructorsAttendanceChannel: instructorsDateRangeAttendanceChannel
                }
            })
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch date range attendance of instructors via channel", err);
        bugsnagClient.notify(err);
    }
    finally {
        if (instructorsDateRangeAttendanceChannel) {
            instructorsDateRangeAttendanceChannel.close();
        }
    }
}


function* getAllEventsOfParticipants(request) {
    try {

        let { firebase, startDate, endDate, accountId, status } = request.payload;

        let endpoint = `bookingoApi/attendance/participants/getAllEvents/${firebase.user.id}?startDate=${startDate}&endDate=${endDate}`;
        if (Helper.getLoggedInUserType(firebase).toLowerCase() === "account" || accountId) {
            endpoint = `bookingoApi/attendance/participants/getAllEvents?startDate=${startDate}&endDate=${endDate}&accountId=${accountId || firebase.user.id}`
        }
        if (status) {
            endpoint += "&status=" + status;
        }
        let response = yield call(callApi, firebase, "get", endpoint, {}, false, null, true);

        if (response.status == 200) {

            let allParticipantsEvents = response.body.data;
            allParticipantsEvents.sort((a, b) => b.date - a.date);
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    isLoading: false,
                    allParticipantsEvents
                }
            });
        }
        else {
            notification('error', formatMsg('failedToGetToEventsOfParticipants'));
            yield put({
                type: actions.ATTENDANCE_SAGA_FAILED
            });
        }
    }
    catch (err) {
        notification('error', formatMsg('failedToGetToEventsOfParticipants'));
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to get all events of participants", err);
        bugsnagClient.notify(err);
    }
}


function* getAllEventsOfInstructors(request) {
    try {
        let { firebase, startDate, endDate, status } = request.payload;

        let endpoint = `bookingoApi/attendance/staff/getAllEvents/${firebase.user.id}?startDate=${startDate}&endDate=${endDate}`;
        if (status) {
            endpoint += "&status=" + status;
        }
        let response = yield call(callApi, firebase, "get", endpoint, {}, false, null, true);

        if (response.status == 200) {

            let allInstructorsEvents = response.body.data;
            allInstructorsEvents.sort((a, b) => b.date - a.date);
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: {
                    isLoading: false,
                    allInstructorsEvents
                }
            });
        }
        else {
            notification('error', formatMsg('failedToGetToEventsOfStaffs'));
            yield put({
                type: actions.ATTENDANCE_SAGA_FAILED
            });
        }
    }
    catch (err) {
        notification('error', formatMsg('failedToGetToEventsOfStaffs'));
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to get all events of participants", err);
        bugsnagClient.notify(err);
    }
}

function* markDateRangeBulkAttendance(actionObj: Record<string, any>) {

    try {
        const { firebase, payload } = actionObj.payload;

        const {
            selectedSchedule,
            tempInstructorsAttendanceMap,
            tempParticipantsAttendanceMap
        } = payload;

        const finalPayload = {
            bulkCommonFields: {
                date: selectedSchedule.date,
                batchId: selectedSchedule.batchId,
                packageId: selectedSchedule.packageId,
                slotRandomId: selectedSchedule.slotRandomId,
                centerId: selectedSchedule.centerId
            },
            instructorAttendance: buildSlotAttendancePayloadArr('INSTRUCTOR', tempInstructorsAttendanceMap),
            participantAttendance: buildSlotAttendancePayloadArr('PARTICIPANT', tempParticipantsAttendanceMap),
            createdBy: firebase.teacher.id,
            createdOn: timeutil.getEpoch()
        }

        let endpoint = 'bookingoApi/attendance/markBulkAttendance';
        let response = yield call(callApi, firebase, "post", endpoint, finalPayload, false);

        if (response.status == 200 && response.body.data.status === "MARKED") {
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { isLoading: false, operationType: "BULK_ATTENDANCE_MARKED_SUCCESSFULLY" }
            });
            notification('success', formatMsg('attendanceMarkedSuccessfully'));
        }
        else {
            yield put({
                type: actions.ATTENDANCE_SAGA_FAILED,
                payload: { operationType: "BULK_ATTENDANCE_MARKING_FAILED" }
            });
            notification('error', formatMsg('failedToMarkAttedance'));
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to mark bulk attendance", err);
        bugsnagClient.notify(err);
    }
}



function* markMultipleEventsBulkAttendance(actionObj: Record<string, any>) {

    try {

        const { firebase, payload, entityType } = actionObj.payload;

        const {
            attendanceMap
        } = payload;

        const newAttendanceMap = new Map();
        for (const [key, value] of attendanceMap.entries()) {
            const [date, slotRandomId, centerId, entityId, batchId, packageId] = key.split('-$-');
            const groupKey = `${date}-$-${slotRandomId}-$-${centerId}-$-${batchId}-$-${packageId}`;
            if (!newAttendanceMap.has(groupKey)) {
                newAttendanceMap.set(groupKey, []);
            }
            newAttendanceMap.get(groupKey).push(value);
        }

        const newAttendanceArray = Array.from(newAttendanceMap.entries()).map(([key, innerArray]) => ({
            key,
            attendance: innerArray
        }));
        const finalPayload = {
            newAttendanceArray,
            entityType,
            createdBy: firebase.teacher.id,
        }

        let endpoint = 'bookingoApi/attendance/markMultipleEventsBulkAttendance';
        let response = yield call(callApi, firebase, "post", endpoint, finalPayload, false);

        if (response.status == 200) {
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { isLoading: false, operationType: "BULK_ATTENDANCE_MARKED_SUCCESSFULLY" }
            });
            notification('success', formatMsg('attendanceMarkedSuccessfully'));
        }
        else {
            yield put({
                type: actions.ATTENDANCE_SAGA_FAILED,
                payload: { operationType: "BULK_ATTENDANCE_MARKING_FAILED" }
            });
            notification('error', formatMsg('failedToMarkAttedance'));
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to mark bulk attendance", err);
        bugsnagClient.notify(err);
    }
}

function* getParticipantsDateRangeAttendanceByAccountId(actionObj: Record<string, any>) {
    try {

        const { firebase, startDate, endDate, accountId } = actionObj.payload;
        let endpoint = `bookingoApi/attendance/participants/getAllAttendanceByAccountId?startDate=${startDate}&endDate=${endDate}&accountId=${accountId}`;
        let response = yield call(callApi, firebase, "get", endpoint, {}, false, false, true);
        if (response.status == 200) {
            let participantsAttendance = response.body.data;
            const participantsAttendanceMap = new Map();
            participantsAttendance.forEach((obj) => participantsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { isLoading: false, participantsAttendanceMap }
            });
        }
        else {
            yield put({ type: actions.ATTENDANCE_SAGA_FAILED });
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch date range attendance of participants by accountId", err);
        bugsnagClient.notify(err);
    }
}

function* getParticipantsScheduleAttendanceByAccountId(actionObj: Record<string, any>) {
    try {
        const { firebase, selectedSchedule, accountId } = actionObj.payload;
        const { slotRandomId, centerId, date } = selectedSchedule;
        const orderByKeyValue = `${timeutil.inFormat(date, "DD-MM-YY")}--${slotRandomId}--${centerId}`;
        let endpoint = `bookingoApi/attendance/participants/getAttendanceByScheduleAndAccountId?orderByKey=${orderByKeyValue}&accountId=${accountId}`;
        let response = yield call(callApi, firebase, "get", endpoint, {}, false, false, true);
        if (response.status == 200) {
            let participantsAttendance = response.body.data;
            const participantsAttendanceMap = new Map();
            participantsAttendance.forEach((obj) => participantsAttendanceMap.set(obj.dateSlotCenterEntityKey, obj));

            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { isLoading: false, participantsAttendanceMap }
            });
        }
        else {
            yield put({ type: actions.ATTENDANCE_SAGA_FAILED });
        }
    }
    catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.ATTENDANCE_SAGA_FAILED
        });
        console.log("Failed to fetch slot  attendance of participants by accountId", err);
        bugsnagClient.notify(err);
    }
}

// A parent marking their own child. The server decides who the caller is, whether the school
// allows it, and what time is recorded; this only says which session and what to mark.
export function* markParentAttendance(actionObj: Record<string, any>) {
    const { firebase, bookingId, slotRandomId, action, time, absentNote } = actionObj.payload;
    try {
        const endpoint = 'bookingoApi/attendance/parent/mark';
        const response = yield call(callApi, firebase, "post", endpoint, { bookingId, slotRandomId, action, time, absentNote }, false);
        if (response.status == 200) {
            yield put({
                type: actions.ATTENDANCE_SAGA_SUCCESS,
                payload: { parentMarking: false, operationType: "PARENT_ATTENDANCE_MARKED" }
            });
        }
        else {
            notification('error', formatMsg('failedToMarkAttedance'));
            yield put({ type: actions.ATTENDANCE_SAGA_FAILED, payload: { parentMarking: false, operationType: "PARENT_ATTENDANCE_MARKING_FAILED" } });
        }
    }
    catch (err) {
        // 4xx carry a reason the parent can act on, e.g. "Already checked out".
        const message = err?.response?.body?.message;
        notification('error', err?.status && err.status < 500 && message ? message : formatMsg('failedToMarkAttedance'));
        yield put({ type: actions.ATTENDANCE_SAGA_FAILED, payload: { parentMarking: false, operationType: "PARENT_ATTENDANCE_MARKING_FAILED" } });
        console.log("Failed to mark parent attendance", err);
        if (!err?.status || err.status >= 500) bugsnagClient.notify(err);
    }
}

export default function* rootSaga() {
    yield takeLatest(actions.GET_PARTICIPANTS_SLOT_ATTENDANCE, getParticipantsSlotAttendance);
    yield takeLatest(actions.GET_INSTRUCTORS_SLOT_ATTENDANCE, getInstructorsSlotAttendance);
    yield takeLatest(actions.MARK_SLOT_BULK_ATTENDANCE, markSlotBulkAttendance);
    yield takeLatest(actions.GET_PARTICIPANTS_DATE_RANGE_ATTENDANCE, getParticipantsDateRangeAttendance);
    yield takeLatest(actions.GET_INSTRUCTORS_DATE_RANGE_ATTENDANCE, getInstructorsDateRangeAttendance);
    yield takeLatest(actions.GET_ALL_EVENTS_OF_PARTICIPANTS, getAllEventsOfParticipants);
    yield takeLatest(actions.GET_ALL_EVENTS_OF_INSTRUCTORS, getAllEventsOfInstructors);
    yield takeLatest(actions.MARK_DATE_RANGE_BULK_ATTENDANCE, markDateRangeBulkAttendance);
    yield takeLatest(actions.MARK_MULTIPLE_EVENTS_BULK_ATTENDANCE, markMultipleEventsBulkAttendance);
    yield takeLatest(actions.GET_PARTICIPANTS_DATE_RANGE_ATTENDANCE_BY_ACCOUNT_ID, getParticipantsDateRangeAttendanceByAccountId);
    yield takeLatest(actions.GET_PARTICIPANTS_SCHEDULE_ATTENDANCE_BY_ACCOUNT_ID, getParticipantsScheduleAttendanceByAccountId)
    yield takeLatest(actions.MARK_PARENT_ATTENDANCE, markParentAttendance);

}